import { useState, useEffect } from 'react'
import { Card, Button, Input, Modal, Table, Loading, Badge, Alert, Select } from '../components/common'
import api from '../services/api'

function UsersPage() {
  const [users, setUsers] = useState([])
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [formData, setFormData] = useState({ username: '', password: '', name: '', role: '', email: '', phone: '', avatar: '👤' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [fetchError, setFetchError] = useState('')

  useEffect(() => { fetchData() }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      setFetchError('')
      
      // Fetch users
      let usersData = []
      try {
        const usersRes = await api.get('/users')
        console.log('Users response:', usersRes.data)
        if (usersRes.data.success) {
          usersData = usersRes.data.data.users || []
        }
      } catch (err) {
        console.error('Error fetching users:', err)
        setFetchError('Gagal memuat data user: ' + (err.response?.data?.message || err.message))
      }
      
      // Fetch roles (for dropdown)
      let rolesData = []
      try {
        const rolesRes = await api.get('/roles-list')
        console.log('Roles response:', rolesRes.data)
        if (rolesRes.data.success) {
          rolesData = rolesRes.data.data.roles || []
        }
      } catch (err) {
        console.error('Error fetching roles:', err)
        // Don't set error, roles is optional for display
      }
      
      setUsers(usersData)
      setRoles(rolesData)
    } catch (err) { 
      console.error('Fetch data error:', err)
      setFetchError('Gagal memuat data: ' + err.message)
    } finally { 
      setLoading(false) 
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.username || !formData.name || !formData.role) { 
      setError('Username, nama, dan role harus diisi')
      return 
    }
    if (!editing && !formData.password) { 
      setError('Password harus diisi untuk user baru')
      return 
    }
    setSaving(true)
    setError('')
    try {
      const payload = { ...formData }
      if (!payload.password) delete payload.password
      
      const response = editing
        ? await api.put(`/users/${editing.id}`, payload)
        : await api.post('/users', payload)
      
      if (response.data.success) { 
        setShowModal(false)
        setEditing(null)
        fetchData()
      } else { 
        setError(response.data.message || 'Gagal menyimpan') 
      }
    } catch (err) { 
      setError(err.response?.data?.message || 'Gagal menyimpan') 
    } finally { 
      setSaving(false) 
    }
  }

  const handleEdit = (item) => {
    setEditing(item)
    setFormData({ 
      username: item.username, 
      password: '', 
      name: item.name, 
      role: item.role, 
      email: item.email || '', 
      phone: item.phone || '', 
      avatar: item.avatar || '👤' 
    })
    setError('')
    setShowModal(true)
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus user ini?')) return
    try {
      await api.delete(`/users/${id}`)
      fetchData()
    } catch (err) { 
      alert(err.response?.data?.message || 'Gagal menghapus') 
    }
  }

  const openAddModal = () => {
    setEditing(null)
    setFormData({ username: '', password: '', name: '', role: '', email: '', phone: '', avatar: '👤' })
    setError('')
    setShowModal(true)
  }

  const columns = [
    { header: 'User', render: (row) => (
      <div className="flex items-center gap-3">
        <span className="text-2xl">{row.avatar || '👤'}</span>
        <div>
          <p className="font-medium">{row.name}</p>
          <p className="text-sm text-gray-500">@{row.username}</p>
        </div>
      </div>
    )},
    { header: 'Role', render: (row) => <Badge variant="primary">{row.role_name || row.role}</Badge> },
    { header: 'Email', accessor: 'email' },
    { header: 'Status', render: (row) => <Badge variant={row.status === 'active' ? 'success' : 'default'}>{row.status}</Badge> },
    { header: 'Aksi', render: (row) => (
      <div className="flex gap-2">
        <button onClick={() => handleEdit(row)} className="text-blue-600 hover:underline text-sm">Edit</button>
        <button onClick={() => handleDelete(row.id)} className="text-red-600 hover:underline text-sm">Hapus</button>
      </div>
    )}
  ]

  const avatars = ['👤', '👨', '👩', '👨‍💼', '👩‍💼', '🧑‍💻', '👷', '🧑‍🏫']

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">👥 Manajemen User</h1>
          <p className="text-gray-500">Kelola akun pengguna sistem</p>
        </div>
        <Button onClick={openAddModal}>➕ Tambah User</Button>
      </div>

      {fetchError && (
        <Alert type="error" onClose={() => setFetchError('')}>{fetchError}</Alert>
      )}

      <Card>
        {loading ? (
          <Loading />
        ) : (
          <Table columns={columns} data={users} emptyText="Belum ada user" />
        )}
      </Card>

      <Modal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)} 
        title={editing ? 'Edit User' : 'Tambah User'} 
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Batal</Button>
            <Button onClick={handleSubmit} loading={saving}>Simpan</Button>
          </>
        }
      >
        {error && <Alert type="error" className="mb-4" onClose={() => setError('')}>{error}</Alert>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input 
              label="Username *" 
              value={formData.username} 
              onChange={(e) => setFormData(p => ({ ...p, username: e.target.value }))} 
              disabled={!!editing} 
            />
            <Input 
              label={editing ? 'Password (kosongkan jika tidak diubah)' : 'Password *'} 
              type="password" 
              value={formData.password} 
              onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))} 
            />
          </div>
          <Input 
            label="Nama Lengkap *" 
            value={formData.name} 
            onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} 
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData(p => ({ ...p, role: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Pilih Role --</option>
              {roles.map(r => (
                <option key={r.role_key} value={r.role_key}>{r.name}</option>
              ))}
            </select>
            {roles.length === 0 && (
              <p className="text-xs text-red-500 mt-1">Tidak ada data role. Pastikan tabel roles terisi.</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input 
              label="Email" 
              type="email" 
              value={formData.email} 
              onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} 
            />
            <Input 
              label="Telepon" 
              value={formData.phone} 
              onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))} 
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Avatar</label>
            <div className="flex gap-2">
              {avatars.map(a => (
                <button 
                  key={a} 
                  type="button" 
                  onClick={() => setFormData(p => ({ ...p, avatar: a }))}
                  className={`text-3xl p-2 rounded-lg ${formData.avatar === a ? 'bg-blue-100 ring-2 ring-blue-500' : 'hover:bg-gray-100'}`}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>
        </form>
      </Modal>
    </div>
  )
}

export default UsersPage
